// gameEngine.js
// Bloom & Blight — pure game logic. Every public function takes a state and
// returns a NEW state (deep-cloned), so React re-renders cleanly and the UI
// layer never mutates game data. After every change we sweep for grown plants:
// a plant whose light >= need and water inside its sweet spot immediately
// grows, leaves play, and awards a petal. First to 4 petals wins.

import {
  SPECIES,
  CARDS,
  BIOMES,
  WEATHER,
  COPIES_FOR_SPECIES_COUNT,
  STANDARD_KIT,
  MAX_LIGHT,
  MAX_WATER,
  GARDEN_SLOTS,
  PETALS_TO_WIN,
  STARTING_HAND,
} from "./gameData";

const clone = (s) => JSON.parse(JSON.stringify(s));

/* ---------------------------------------------------------------- helpers */

const shuffle = (arr, rng) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const addLog = (s, entry) => {
  s.log.push({ id: s.log.length, turn: s.turn, ...entry });
};

// Log-voice helpers. In solo play the human is literally named "You", so
// their lines read second person ("You watered your Snake Plant") while
// Sprout's stay third person. Online matches use real names for both
// players, so every line reads third person for both viewers.
const their = (player) => (player.name === "You" ? "your" : "their");
const owns = (player) => (player.name === "You" ? "your" : `${player.name}'s`);
const cap = (str) => str.charAt(0).toUpperCase() + str.slice(1);

const findPlant = (s, plantUid) => {
  for (let p = 0; p < 2; p++) {
    const idx = s.players[p].garden.findIndex(
      (pl) => pl && pl.uid === plantUid
    );
    if (idx !== -1) return { owner: p, slot: idx, plant: s.players[p].garden[idx] };
  }
  return null;
};

const handCard = (player, handUid) =>
  player.hand.find((c) => c.uid === handUid) || null;

const removeFromHand = (player, handUid) => {
  const i = player.hand.findIndex((c) => c.uid === handUid);
  return i === -1 ? null : player.hand.splice(i, 1)[0];
};

// Card effects and manual watering still work through a Humidifier; only
// weather is blocked by that status.
const changeWater = (plant, delta) => {
  if (delta === 0) return false;
  const next = Math.max(0, Math.min(MAX_WATER, plant.water + delta));
  const changed = next !== plant.water;
  plant.water = next;
  return changed;
};

const changeLight = (plant, delta) => {
  plant.light = Math.max(0, Math.min(MAX_LIGHT, plant.light + delta));
};

export const isGrown = (plant) => {
  const sp = SPECIES[plant.speciesId];
  return (
    plant.light >= sp.lightNeed &&
    plant.water >= sp.waterMin &&
    plant.water <= sp.waterMax
  );
};

// Sweep both gardens for plants that hit their targets. Grown plants leave
// play (they can no longer be affected) and award a petal.
const sweepGrowth = (s) => {
  for (let p = 0; p < 2; p++) {
    const player = s.players[p];
    player.garden.forEach((plant, idx) => {
      if (plant && isGrown(plant)) {
        player.garden[idx] = null;
        player.petals += 1;
        player.grownSpecies.push(plant.speciesId);
        addLog(s, {
          actor: player.name,
          icon: "🌸",
          kind: "grow",
          text: `${cap(owns(player))} ${SPECIES[plant.speciesId].name} grew up! (${player.petals}/${PETALS_TO_WIN} petals)`,
        });
        if (player.petals >= PETALS_TO_WIN && s.phase !== "over") {
          s.phase = "over";
          s.winner = p;
          addLog(s, {
            actor: player.name,
            icon: "🏆",
            kind: "grow",
            text: `${cap(owns(player))} flower is in full bloom — ${player.name === "You" ? "you win!" : `${player.name} wins!`}`,
          });
        }
      }
    });
  }
};

const drawCards = (s, pIdx, n, quiet = false) => {
  const player = s.players[pIdx];
  let drawn = 0;
  for (let i = 0; i < n; i++) {
    if (player.deck.length === 0 && player.discard.length > 0) {
      player.deck = shuffle(player.discard, Math.random);
      player.discard = [];
      addLog(s, {
        actor: player.name,
        icon: "🔄",
        kind: "system",
        text: `${player.name} shuffled ${their(player)} discard pile back into a fresh deck.`,
      });
    }
    if (player.deck.length === 0) break;
    player.hand.push(player.deck.pop());
    drawn++;
  }
  if (!quiet && drawn > 0) {
    addLog(s, {
      actor: player.name,
      icon: "🃏",
      kind: "system",
      text: `${player.name} drew ${drawn === 1 ? "a card" : `${drawn} cards`}.`,
    });
  }
  return drawn;
};

/* ------------------------------------------------------------- new game */

let uidCounter = 0;
const makeCard = (id) => ({ uid: `c${uidCounter++}`, id });

const buildDeck = (speciesIds, rng) => {
  const copies = COPIES_FOR_SPECIES_COUNT[speciesIds.length];
  const cards = [
    ...speciesIds.flatMap((id) => Array(copies).fill(id)),
    ...STANDARD_KIT,
  ].map(makeCard);
  return shuffle(cards, rng);
};

const makePlayer = (name, isAI, speciesIds, rng) => ({
  name,
  isAI,
  speciesIds,
  deck: buildDeck(speciesIds, rng),
  hand: [],
  discard: [],
  garden: Array(GARDEN_SLOTS).fill(null),
  petals: 0,
  grownSpecies: [],
  usedPlantAction: false,
  usedWater: false,
});

export const rollBiome = (rng = Math.random) => {
  const ids = Object.keys(BIOMES);
  return ids[Math.floor(rng() * ids.length)];
};

export const rollWeather = (biomeId, rng = Math.random) => {
  const odds = BIOMES[biomeId].odds;
  const roll = Math.floor(rng() * 6);
  return roll < odds.sunny
    ? "sunny"
    : roll < odds.sunny + odds.cloudy
      ? "cloudy"
      : "rain";
};

// names/vsAI let the multiplayer backend build a two-human match with real
// gardener names; solo play keeps the "You" vs Sprout defaults.
export const newGame = ({
  playerSpecies,
  aiSpecies,
  biomeId,
  rng = Math.random,
  names = ["You", "Sprout"],
  vsAI = true,
}) => {
  uidCounter = 0;
  const s = {
    phase: "play",
    biomeId,
    turn: 1,
    active: 0,
    winner: null,
    lastWeather: null, // { id, key } — key bumps so the UI can animate repeats
    weatherCount: 0,
    nextForecast: rollWeather(biomeId, rng),
    players: [
      makePlayer(names[0], false, playerSpecies, rng),
      makePlayer(names[1], vsAI, aiSpecies, rng),
    ],
    log: [],
  };
  addLog(s, {
    actor: "system",
    icon: BIOMES[biomeId].icon,
    kind: "system",
    text: `The match sprouts in ${BIOMES[biomeId].name}! First gardener to grow ${PETALS_TO_WIN} plants wins.`,
  });
  drawCards(s, 0, STARTING_HAND, true);
  drawCards(s, 1, STARTING_HAND, true);
  addLog(s, {
    actor: "system",
    icon: "🃏",
    kind: "system",
    text: "Both gardeners drew an opening hand of 5.",
  });
  return s;
};

// A deliberately mid-match state: every major interaction is available
// immediately, without asking a reviewer to play through several empty turns.
export const createDemoGame = () => {
  const rngValues = [0.12, 0.72, 0.34, 0.91, 0.48, 0.23];
  let cursor = 0;
  const rng = () => rngValues[cursor++ % rngValues.length];
  const s = newGame({
    playerSpecies: ["calathea", "snake", "zz"],
    aiSpecies: ["cactus", "pilea", "snake"],
    biomeId: "carmel",
    rng,
  });
  s.turn = 4;
  s.active = 0;
  s.nextForecast = "cloudy";
  s.players[0].petals = 2;
  s.players[0].grownSpecies = ["zz", "snake"];
  s.players[0].garden = [
    { uid: "demo-calathea", speciesId: "calathea", light: 5, water: 3, umbrella: false, growLamp: true, humidifier: false },
    { uid: "demo-snake", speciesId: "snake", light: 3, water: 1, umbrella: true, growLamp: false, humidifier: false },
    null,
  ];
  s.players[0].hand = [
    { uid: "demo-zz-card", id: "zz" },
    { uid: "demo-fertilizer", id: "fertilizer" },
    { uid: "demo-irrigation", id: "irrigation" },
    { uid: "demo-rootrot", id: "rootRot" },
    { uid: "demo-pests", id: "pests" },
    { uid: "demo-greenhouse", id: "greenhouse" },
  ];
  s.players[1].petals = 1;
  s.players[1].grownSpecies = ["pilea"];
  s.players[1].garden = [
    { uid: "demo-cactus", speciesId: "cactus", light: 5, water: 1, umbrella: false, growLamp: false, humidifier: true },
    { uid: "demo-pilea", speciesId: "pilea", light: 3, water: 2, umbrella: false, growLamp: false, humidifier: false },
    null,
  ];
  s.players[1].hand = [
    { uid: "ai-fertilizer", id: "fertilizer" },
    { uid: "ai-umbrella", id: "umbrella" },
    { uid: "ai-snake", id: "snake" },
  ];
  s.log.push({
    id: s.log.length,
    turn: 4,
    actor: "system",
    icon: "✨",
    kind: "system",
    text: "Demo garden loaded. Grow two more plants before Sprout catches up!",
  });
  return s;
};

const canAct = (s, pIdx) =>
  s.phase === "play" && s.active === pIdx && pIdx >= 0 && pIdx < s.players.length;

/* ------------------------------------------------------------- actions */
// All action functions validate, log, sweep growth, and return a new state.
// On an invalid request they return the original state untouched.

export const plantCard = (state, pIdx, handUid, slotIdx) => {
  const s = clone(state);
  const player = s.players[pIdx];
  const card = handCard(player, handUid);
  if (
    !canAct(s, pIdx) ||
    !card ||
    CARDS[card.id].type !== "plant" ||
    player.usedPlantAction ||
    player.garden[slotIdx]
  )
    return state;

  removeFromHand(player, handUid);
  player.garden[slotIdx] = {
    uid: card.uid,
    speciesId: card.id,
    light: 0,
    water: 0,
    umbrella: false,
    growLamp: false,
    humidifier: false,
  };
  player.usedPlantAction = true;
  addLog(s, {
    actor: player.name,
    icon: "🌱",
    kind: "action",
    card: card.id,
    text: `${player.name} planted a ${SPECIES[card.id].name}.`,
  });
  sweepGrowth(s);
  return s;
};

// Species-as-treatment: compost a plant card onto a same-species plant of
// yours for +1 light and +1 water. Plays like a treatment (unlimited).
export const compostCard = (state, pIdx, handUid, plantUid) => {
  const s = clone(state);
  const player = s.players[pIdx];
  const card = handCard(player, handUid);
  const hit = findPlant(s, plantUid);
  if (
    !canAct(s, pIdx) ||
    !card ||
    !hit ||
    hit.owner !== pIdx ||
    CARDS[card.id].type !== "plant" ||
    hit.plant.speciesId !== card.id
  )
    return state;

  removeFromHand(player, handUid);
  player.discard.push(card);
  changeLight(hit.plant, 1);
  const watered = changeWater(hit.plant, 1);
  addLog(s, {
    actor: player.name,
    icon: "🍂",
    kind: "action",
    card: card.id,
    text: `${player.name} composted a spare ${SPECIES[card.id].name} onto ${their(player)} ${SPECIES[card.id].name}: +1 ☀️${watered ? ", +1 💧" : " (pot already full)"}.`,
  });
  sweepGrowth(s);
  return s;
};

export const waterPlant = (state, pIdx, plantUid) => {
  const s = clone(state);
  const player = s.players[pIdx];
  const hit = findPlant(s, plantUid);
  if (!canAct(s, pIdx) || player.usedWater || !hit || hit.owner !== pIdx)
    return state;

  player.usedWater = true;
  const watered = changeWater(hit.plant, 1);
  addLog(s, {
    actor: player.name,
    icon: "💧",
    kind: "action",
    text: watered
      ? `${player.name} watered ${their(player)} ${SPECIES[hit.plant.speciesId].name}.`
      : `${player.name} tried to water ${their(player)} ${SPECIES[hit.plant.speciesId].name}, but the pot is already full.`,
  });
  sweepGrowth(s);
  return s;
};

// Shares the once-per-turn plant action: plant a plant OR dig one up.
// Digging up returns the plant card to your deck (shuffled in) — blights,
// statuses, and all progress are lost with the pot.
export const digUpPlant = (state, pIdx, plantUid) => {
  const s = clone(state);
  const player = s.players[pIdx];
  const hit = findPlant(s, plantUid);
  if (!canAct(s, pIdx) || player.usedPlantAction || !hit || hit.owner !== pIdx)
    return state;

  player.usedPlantAction = true;
  player.garden[hit.slot] = null;
  player.deck.push({ uid: hit.plant.uid, id: hit.plant.speciesId });
  player.deck = shuffle(player.deck, Math.random);
  addLog(s, {
    actor: player.name,
    icon: "🪏",
    kind: "action",
    text: `${player.name} dug up ${their(player)} ${SPECIES[hit.plant.speciesId].name}${hit.plant.umbrella ? " (umbrella and all)" : ""} — shuffled back into ${their(player)} deck.`,
  });
  sweepGrowth(s);
  return s;
};

/* ---- treatments ---- */

export const playTreatment = (state, pIdx, handUid, payload = {}) => {
  const s = clone(state);
  const player = s.players[pIdx];
  const card = handCard(player, handUid);
  if (!canAct(s, pIdx) || !card || CARDS[card.id].type !== "treatment")
    return state;

  const spend = () => {
    removeFromHand(player, handUid);
    player.discard.push(card);
  };
  const name = (plant) => SPECIES[plant.speciesId].name;
  const logMark = s.log.length; // tag this play's action entry with its card

  switch (card.id) {
    case "fertilizer": {
      const hit = findPlant(s, payload.plantUid);
      if (!hit || hit.owner !== pIdx) return state;
      const waterChoice = payload.water ?? 1;
      if (![0, 1].includes(waterChoice)) return state;
      spend();
      changeLight(hit.plant, 1);
      const watered = waterChoice === 0 ? false : changeWater(hit.plant, 1);
      addLog(s, {
        actor: player.name,
        icon: "🧪",
        kind: "action",
        text: `${player.name} fertilized ${their(player)} ${name(hit.plant)}: +1 ☀️${watered ? ", +1 💧" : ""}.`,
      });
      break;
    }
    case "growLamp": {
      const hit = findPlant(s, payload.plantUid);
      if (!hit || hit.owner !== pIdx || hit.plant.growLamp) return state;
      spend();
      hit.plant.growLamp = true;
      addLog(s, {
        actor: player.name,
        icon: "🔦",
        kind: "action",
        text: `${player.name} set up a Grow Lamp over ${their(player)} ${name(hit.plant)} — +1 ☀️ every forecast.`,
      });
      break;
    }
    case "irrigation": {
      // payload: { plantUid, amount } — ONE of your plants, ±1–3 💧
      // (positive pours, negative drains).
      const amount = payload.amount;
      const hit = findPlant(s, payload.plantUid);
      if (!hit || hit.owner !== pIdx) return state;
      if (!Number.isInteger(amount) || amount === 0 || Math.abs(amount) > 3) return state;
      spend();
      const ok = changeWater(hit.plant, amount);
      addLog(s, {
        actor: player.name,
        icon: "🚿",
        kind: "action",
        text: `${player.name} ran the irrigation on ${their(player)} ${name(hit.plant)}: ${ok ? (amount > 0 ? `+${amount}` : `−${-amount}`) : "±0"} 💧.`,
      });
      break;
    }
    case "repot": {
      const hit = findPlant(s, payload.plantUid);
      if (!hit || hit.owner !== pIdx || hit.plant.light < 3) return state;
      spend();
      const hadUmbrella = hit.plant.umbrella;
      hit.plant.umbrella = false;
      let bonusText;
      if (payload.bonus === "water") {
        const ok = changeWater(hit.plant, 1);
        bonusText = ok ? "+1 💧" : "+0 💧 (pot full)";
      } else if (payload.bonus === "drain") {
        const before = hit.plant.water;
        changeWater(hit.plant, -3);
        bonusText = `fresh soil drained −${before - hit.plant.water} 💧`;
      } else {
        changeLight(hit.plant, 1);
        bonusText = "+1 ☀️";
      }
      addLog(s, {
        actor: player.name,
        icon: "🪴",
        kind: "action",
        text: `${player.name} repotted ${their(player)} ${name(hit.plant)}: ${hadUmbrella ? "blight cleared, " : ""}${bonusText}.`,
      });
      break;
    }
    case "humidifier": {
      const hit = findPlant(s, payload.plantUid);
      if (!hit || hit.owner !== pIdx || hit.plant.humidifier) return state;
      spend();
      hit.plant.humidifier = true;
      addLog(s, {
        actor: player.name,
        icon: "🫧",
        kind: "action",
        text: `${player.name} placed a Humidifier by ${their(player)} ${name(hit.plant)} — the weather can no longer change its 💧 (${hit.plant.water}).`,
      });
      break;
    }
    case "shopping": {
      spend();
      addLog(s, {
        actor: player.name,
        icon: "🛍️",
        kind: "action",
        text: `${player.name} went on a shopping trip.`,
      });
      drawCards(s, pIdx, 2);
      break;
    }
    case "recycle": {
      const i = player.discard.findIndex((c) => c.uid === payload.discardUid);
      if (i === -1) return state;
      const target = player.discard[i];
      if (CARDS[target.id].type === "blight") return state;
      spend();
      // spend() pushed Recycle onto the discard — retrieve the target after.
      const j = player.discard.findIndex((c) => c.uid === payload.discardUid);
      player.discard.splice(j, 1);
      player.hand.push(target);
      addLog(s, {
        actor: player.name,
        icon: "♻️",
        kind: "action",
        text: `${player.name} recycled ${CARDS[target.id].name} back into ${their(player)} hand.`,
      });
      break;
    }
    case "planter": {
      const slotIdx = payload.slotIdx;
      if (player.usedPlantAction || player.garden[slotIdx]) return state;
      const i = player.deck.findIndex((c) => c.uid === payload.deckUid);
      if (i === -1 || CARDS[player.deck[i].id].type !== "plant") return state;
      spend();
      const plantC = player.deck.splice(i, 1)[0];
      player.deck = shuffle(player.deck, Math.random);
      player.garden[slotIdx] = {
        uid: plantC.uid,
        speciesId: plantC.id,
        light: 0,
        water: 0,
        umbrella: false,
        growLamp: false,
        humidifier: false,
      };
      player.usedPlantAction = true;
      addLog(s, {
        actor: player.name,
        icon: "🏺",
        kind: "action",
        text: `${player.name} used a Planter to plant a ${SPECIES[plantC.id].name} straight from the deck.`,
      });
      break;
    }
    case "greenhouse": {
      // payload.ops: [{ plantUid, stat: 'light'|'water', delta: 1|-1 }] —
      // up to 3 ops, own plants only, at most ONE adjustment per plant.
      // Greenhouse can add light or adjust water in either direction; it
      // cannot remove light.
      const ops = payload.ops || [];
      if (ops.length < 1) return state;
      if (ops.length > 3) return state;
      if (new Set(ops.map((op) => op.plantUid)).size !== ops.length) return state;
      for (const op of ops) {
        const target = findPlant(s, op.plantUid);
        if (
          !target ||
          target.owner !== pIdx ||
          !["light", "water"].includes(op.stat) ||
          ![-1, 1].includes(op.delta) ||
          (op.stat === "light" && op.delta !== 1)
        ) return state;
      }
      spend();
      const parts = [];
      for (const op of ops) {
        const hit = findPlant(s, op.plantUid);
        const sign = op.delta > 0 ? "+" : "−";
        if (op.stat === "light") {
          changeLight(hit.plant, op.delta);
          parts.push(`${owns(s.players[hit.owner])} ${name(hit.plant)} ${sign}1 ☀️`);
        } else {
          const ok = changeWater(hit.plant, op.delta);
          parts.push(
            `${owns(s.players[hit.owner])} ${name(hit.plant)} ${ok ? sign + "1" : "±0"} 💧`
          );
        }
      }
      addLog(s, {
        actor: player.name,
        icon: "🏡",
        kind: "action",
        text: `${player.name} adjusted the greenhouse: ${parts.join(", ")}.`,
      });
      break;
    }
    default:
      return state;
  }

  if (s.log.length > logMark) s.log[logMark].card = card.id;
  sweepGrowth(s);
  return s;
};

/* ---- blights ---- */

export const playBlight = (state, pIdx, handUid, payload = {}) => {
  const s = clone(state);
  const player = s.players[pIdx];
  const card = handCard(player, handUid);
  if (!canAct(s, pIdx) || !card || CARDS[card.id].type !== "blight")
    return state;

  const hit = findPlant(s, payload.plantUid);
  if (!hit || hit.owner === pIdx) return state; // blights hit enemy plants only
  const enemy = s.players[hit.owner];
  const plantName = SPECIES[hit.plant.speciesId].name;

  const spend = () => {
    removeFromHand(player, handUid);
    player.discard.push(card);
  };
  const logMark = s.log.length;

  switch (card.id) {
    case "umbrella": {
      if (hit.plant.umbrella) return state;
      spend();
      hit.plant.umbrella = true;
      addLog(s, {
        actor: player.name,
        icon: "☂️",
        kind: "blight",
        text: `${player.name} opened an Umbrella over ${owns(enemy)} ${plantName} — it's cut off from the weather!`,
      });
      break;
    }
    case "rootRot": {
      const amount = 5;
      spend();
      const ok = changeWater(hit.plant, amount);
      addLog(s, {
        actor: player.name,
        icon: "🦠",
        kind: "blight",
        text: ok
          ? `${player.name} infected ${owns(enemy)} ${plantName} with Root Rot: +${amount} 💧!`
          : `${player.name} used Root Rot on ${owns(enemy)} ${plantName}, but its water was already full.`,
      });
      break;
    }
    case "shovel": {
      if (hit.plant.light >= 4 || hit.plant.water >= 4) return state;
      spend();
      enemy.garden[hit.slot] = null;
      enemy.discard.push({ uid: hit.plant.uid, id: hit.plant.speciesId });
      addLog(s, {
        actor: player.name,
        icon: "🪓",
        kind: "blight",
        text: `${player.name} dug up ${owns(enemy)} ${plantName} with a Shovel! It tumbles into ${owns(enemy)} discard pile.`,
      });
      break;
    }
    case "pests": {
      spend();
      hit.plant.light = 0;
      const dried = hit.plant.water > 0;
      hit.plant.water = 0;
      addLog(s, {
        actor: player.name,
        icon: "🐛",
        kind: "blight",
        text: `${player.name} released Pests on ${owns(enemy)} ${plantName} — ☀️ and 💧 reset to 0${dried ? "" : " (water was already 0)"}.`,
      });
      break;
    }
    default:
      return state;
  }

  if (s.log.length > logMark) s.log[logMark].card = card.id;
  sweepGrowth(s);
  return s;
};

/* ---- end of turn: forecast roll, weather, pass the trowel ---- */

export const endTurn = (state, rng = Math.random) => {
  const s = clone(state);
  if (s.phase !== "play") return state;
  const ending = s.players[s.active];

  // Resolve the forecast players have been able to plan around.
  const weatherId = s.nextForecast;
  const w = WEATHER[weatherId];

  addLog(s, {
    actor: ending.name,
    icon: "🎲",
    kind: "weather",
    text: `${ending.name} ended ${their(ending)} turn — ${w.icon} ${w.name.toUpperCase()}: ${w.text}`,
  });

  // Weather hits every plant in both gardens (umbrellas block it entirely).
  for (let p = 0; p < 2; p++) {
    for (const plant of s.players[p].garden) {
      if (!plant) continue;
      if (!plant.umbrella) {
        changeLight(plant, w.light);
        if (!plant.humidifier) changeWater(plant, w.water);
      }
      if (plant.growLamp) changeLight(plant, 1); // lamps shine rain or shine
    }
  }
  s.lastWeather = { id: weatherId, key: ++s.weatherCount };
  sweepGrowth(s);
  if (s.phase === "over") return s;

  // Pass the trowel.
  s.active = 1 - s.active;
  if (s.active === 0) s.turn += 1;
  const next = s.players[s.active];
  next.usedPlantAction = false;
  next.usedWater = false;
  s.nextForecast = rollWeather(s.biomeId, rng);
  drawCards(s, s.active, 1);
  return s;
};

/* ------------------------------------------------- UI helper: targeting */
// Which plants can this card legally target right now?
export const getTargets = (state, pIdx, cardId) => {
  const mine = state.players[pIdx].garden.filter(Boolean);
  const theirs = state.players[1 - pIdx].garden.filter(Boolean);
  const card = CARDS[cardId];
  if (!card) return [];

  if (card.type === "plant") return mine.filter((p) => p.speciesId === cardId);
  if (card.type === "blight") {
    switch (cardId) {
      case "umbrella":
        return theirs.filter((p) => !p.umbrella);
      case "shovel":
        return theirs.filter((p) => p.light < 4 && p.water < 4);
      case "rootRot":
      case "pests":
        return theirs;
      default:
        return [];
    }
  }
  switch (cardId) {
    case "fertilizer":
    case "irrigation":
      return mine;
    case "growLamp":
      return mine.filter((p) => !p.growLamp);
    case "humidifier":
      return mine.filter((p) => !p.humidifier);
    case "repot":
      return mine.filter((p) => p.light >= 3);
    case "greenhouse":
      return mine;
    default:
      return [];
  }
};
